import { Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, Pool, Position } from '@kodiak-finance/v3-sdk'
import { PositionDetails } from 'types/position'

export type PositionTokenAmounts = {
  amount0: number
  amount1: number
}

export const getPositionTokenAmounts = (
  positionDetails: PositionDetails,
  chainId: number
): PositionTokenAmounts | null => {
  const liquidity = positionDetails.liquidity
  if (!liquidity || liquidity === '0') return null

  try {
    const token0 = new Token(
      chainId,
      positionDetails.token0,
      positionDetails.token0Decimals || 18,
      positionDetails.token0Symbol
    )
    const token1 = new Token(
      chainId,
      positionDetails.token1,
      positionDetails.token1Decimals || 18,
      positionDetails.token1Symbol
    )
    if (token0.equals(token1)) return null

    const [sortedToken0, sortedToken1] = token0.sortsBefore(token1) ? [token0, token1] : [token1, token0]
    const pool = new Pool(
      sortedToken0,
      sortedToken1,
      positionDetails.fee as FeeAmount,
      positionDetails.pool.sqrtPrice,
      positionDetails.pool.liquidity,
      positionDetails.poolTick ?? 0
    )
    const v3Position = new Position({
      pool,
      liquidity,
      tickLower: positionDetails.tickLower,
      tickUpper: positionDetails.tickUpper,
    })

    const amount0 = parseFloat((token0.equals(sortedToken0) ? v3Position.amount0 : v3Position.amount1).toExact())
    const amount1 = parseFloat((token1.equals(sortedToken1) ? v3Position.amount1 : v3Position.amount0).toExact())

    return { amount0, amount1 }
  } catch (error) {
    console.error('Error calculating position token amounts:', error)
    return null
  }
}

export const getPositionUsdValue = (
  positionDetails: PositionDetails,
  chainId: number,
  priceMap: Record<string, number>,
  precomputedAmounts?: PositionTokenAmounts | null
): number => {
  const amounts = precomputedAmounts ?? getPositionTokenAmounts(positionDetails, chainId)
  if (!amounts) return 0

  const price0 = priceMap[positionDetails.token0.toLowerCase()] || 0
  const price1 = priceMap[positionDetails.token1.toLowerCase()] || 0

  return amounts.amount0 * price0 + amounts.amount1 * price1
}
